import { Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { capture } from "@/lib/posthog";
import { salesWhatsappLink } from "@/lib/whatsapp";
import { WHATSAPP_BTN, WhatsappIcon } from "@/components/common/whatsapp-brand";
import { useActiveProfile } from "@/providers/ActiveProfileProvider";
import { useInterests } from "@/hooks/queries/useInterests";

const HELP_LINK = salesWhatsappLink(
  "Hola, quiero ayuda para configurar mi línea de negocio en LicitiGo.",
);

/**
 * Rescate para quien dejó su línea vacía: aparece en el Descubrimiento cuando la
 * cuenta sigue sin decir qué vende, que es justo cuando el listado se siente
 * inútil. El onboarding se hace una sola vez, así que la ayuda es por WhatsApp.
 * Se apaga solo al agregar el primer término.
 *
 * Con más de una línea de negocio no se muestra: quien llegó a crear la segunda
 * ya entendió el producto y dejarla vacía es una decisión suya. Tampoco en una
 * línea con el análisis de calificación: filtra por zona, y el wizard le pondría recursos.
 */
export function ConfigureLineBanner() {
  const { profiles, activeProfile, isLoading } = useActiveProfile();
  const { data: interests } = useInterests(activeProfile?.searchprofile_id);

  const keywords = activeProfile?.searchprofile_keywords ?? [];
  // `<= 1` y no `=== 1`: cerrar el wizard en la bienvenida deja la cuenta con
  // CERO líneas (la crea el paso 2), y ese es justo el que hay que rescatar.
  const unconfigured =
    !isLoading &&
    !activeProfile?.searchprofile_checkqualification &&
    profiles.length <= 1 &&
    keywords.length === 0 &&
    (interests?.length ?? 0) === 0;

  if (!unconfigured) return null;

  return (
    <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:flex-row sm:items-center">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-600">
        <Sparkles className="size-5 text-white" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-bold text-slate-900">
          Todavía no nos has dicho qué vendes
        </p>
        <p className="mt-0.5 text-sm leading-relaxed text-slate-600">
          Sin eso te mostramos todas las licitaciones, sin marcar cuáles
          coinciden contigo. Escríbenos y te ayudamos a configurarla.
        </p>
      </div>
      <a
        href={HELP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => capture("configure_line_banner_clicked", { channel: "whatsapp" })}
        className={cn(WHATSAPP_BTN, "shrink-0 cursor-pointer")}
      >
        <WhatsappIcon />
        Escríbenos
      </a>
    </div>
  );
}
